/**
 * socket base class
 * 不同平台 socket 的 api 不同但功能相同，这里抽象一层
 */
import { getRandomId, psLog, stringifyData } from './utils';
import { makeMessage, makeBroadcastMessage, makeUnicastMessage, } from './message';
import * as SERVER_MESSAGE_TYPE from './message/server-type';
import { atom } from './atom';
import { SOCKET_CONFIG } from './constants';
// fork WebSocket state
export var SocketState;
(function (SocketState) {
    SocketState[SocketState["CONNECTING"] = 0] = "CONNECTING";
    SocketState[SocketState["OPEN"] = 1] = "OPEN";
    SocketState[SocketState["CLOSING"] = 2] = "CLOSING";
    SocketState[SocketState["CLOSED"] = 3] = "CLOSED";
})(SocketState || (SocketState = {}));
// Caps exponential backoff after SOCKET_CONFIG.MAX_RETRY_ATTEMPTS increases.
const MAX_RETRY_INTERVAL = Math.pow(SOCKET_CONFIG.RETRY_INTERVAL_MULTIPLIER, SOCKET_CONFIG.MAX_RETRY_ATTEMPTS) * SOCKET_CONFIG.INITIAL_RETRY_INTERVAL_MS;
// 封装不同平台的 socket
export class SocketWrapper {
    events = {
        open: [],
        close: [],
        error: [],
        message: [],
    };
    emit(event, data) {
        this.events[event].forEach((fun) => {
            fun(data);
        });
        // for close and error, clear all listeners or they will be called on next socket instance.
        if (event === 'close' || event === 'error') {
            this.clearListeners();
        }
    }
    onOpen(fun) {
        this.events.open.push(fun);
    }
    onClose(fun) {
        this.events.close.push(fun);
    }
    onError(fun) {
        this.events.error.push(fun);
    }
    onMessage(fun) {
        this.events.message.push(fun);
    }
    clearListeners() {
        // clear listeners
        Object.entries(this.events).forEach(([, funs]) => {
            funs.splice(0);
        });
    }
}
export class SocketStoreBase {
    socketUrl = '';
    socketConnection = null;
    debuggerConnection = null;
    // ping timer used for send next ping.
    // a ping is sent after last msg (normal msg or pong) received.
    pingTimer = null;
    // pong timer used for waiting for pong, if pong not received, close the connection
    pongTimer = null;
    retryTimer = null;
    // Cache messages only in online mode
    isOffline = false;
    // Maximum message buffer size (0 = unlimited).
    // When limit is reached, oldest messages are evicted using a sliding window approach.
    messageCapacity = 0;
    // Message buffer implementing FIFO eviction
    messages = [];
    // Index of the first valid message (avoids O(n) array shifts on every eviction)
    messageHead = 0;
    // events center
    events = {
        debug: [],
        refresh: [],
        'atom-detail': [],
        'atom-getter': [],
        'debugger-online': [],
        'database-pagination': [],
        'public-data': [],
        'harbor-clear': [],
    };
    // Starts at the configured delay and increases with exponential backoff.
    retryInterval = SOCKET_CONFIG.INITIAL_RETRY_INTERVAL_MS;
    connectable = true;
    getSocket() {
        return this.socketWrapper;
    }
    getPageSpyConfig = null;
    getClient = null;
    updateRoomInfo() {
        if (this.getPageSpyConfig) {
            const { project, title } = this.getPageSpyConfig();
            const name = this.getClient?.().getName();
            this.send({
                type: SERVER_MESSAGE_TYPE.UPDATE_ROOM_INFO,
                content: {
                    info: {
                        name,
                        group: project,
                        tags: {
                            title,
                            name,
                            group: project,
                        },
                    },
                },
            }, true);
        }
    }
    // response message filters, to handle some wired messages
    static messageFilters = [];
    constructor() {
        this.addListener('atom-detail', SocketStoreBase.handleResolveAtom);
        this.addListener('atom-getter', SocketStoreBase.handleAtomPropertyGetter);
        this.addListener('debugger-online', this.handleFlushBuffer);
    }
    async init(url) {
        try {
            if (!url) {
                throw Error('WebSocket url cannot be empty');
            }
            this.socketWrapper.clearListeners();
            // close existing connection
            if (this.socketWrapper.getState() === SocketState.OPEN) {
                // make sure the existing connection closed.
                // we need to register new handlers immediately.
                await new Promise((resolve) => {
                    this.socketWrapper.onClose(() => {
                        this.socketWrapper.clearListeners();
                        resolve();
                    });
                    this.socketWrapper.close();
                });
            }
            this.socketWrapper?.onOpen(() => {
                this.connectOnline();
            });
            // Strictly, the onMessage should be called after onOpen. But for some platform(alipay,)
            // this may cause some message losing.
            this.socketWrapper?.onMessage((evt) => {
                this.handleMessage(evt);
            });
            this.socketWrapper?.onClose(() => {
                this.connectOffline();
            });
            this.socketWrapper?.onError(() => {
                // we treat on error the same with on close.
                this.connectOffline();
            });
            this.socketUrl = url;
            this.socketWrapper?.init(url);
        }
        catch (e) {
            psLog.error(e instanceof Error ? e.message : String(e));
        }
    }
    addListener(type, fn) {
        /* c8 ignore next 3 */
        if (!this.events[type]) {
            this.events[type] = [];
        }
        this.events[type].push(fn);
    }
    removeListener(type, fn) {
        /* c8 ignore next 3 */
        const fns = this.events[type] || [];
        const index = fns.indexOf(fn);
        if (index > -1) {
            fns.splice(index, 1);
        }
    }
    broadcastMessage(msg, noCache = false) {
        const message = makeBroadcastMessage(msg);
        this.send(message, noCache);
    }
    close() {
        this.connectable = false;
        this.clearPing();
        if (this.retryTimer) {
            clearTimeout(this.retryTimer);
            this.retryTimer = null;
        }
        this.socketWrapper?.close();
        this.messages = [];
        this.messageHead = 0;
        Object.entries(this.events).forEach(([evt, fns]) => {
            // 这三个事件的生命周期跟随 socketStore
            if (['atom-detail', 'atom-getter', 'debugger-online'].includes(evt)) {
                return;
            }
            fns.splice(0);
        });
    }
    connectOnline() {
        this.retryInterval = SOCKET_CONFIG.INITIAL_RETRY_INTERVAL_MS;
        this.updateRoomInfo();
        this.ping();
    }
    connectOffline() {
        this.socketConnection = null;
        this.debuggerConnection = null;
        this.clearPing();
        if (this.retryTimer) {
            clearTimeout(this.retryTimer);
        }
        if (!this.connectable)
            return;
        this.retryTimer = setTimeout(() => {
            if (this.retryInterval < MAX_RETRY_INTERVAL) {
                this.retryInterval *= SOCKET_CONFIG.RETRY_INTERVAL_MULTIPLIER;
            }
            this.retryTimer = null;
            this.tryReconnect();
        }, this.retryInterval);
    }
    tryReconnect() {
        this.init(this.socketUrl);
    }
    ping() {
        if (this.pingTimer) {
            clearTimeout(this.pingTimer);
        }
        if (this.pongTimer) {
            clearTimeout(this.pongTimer);
        }
        /* c8 ignore start */
        this.pingTimer = setTimeout(() => {
            this.send({
                type: 'ping',
                content: null,
            });
            this.pingTimer = null;
            this.pongTimer = setTimeout(() => {
                // lost connection
                this.connectOffline();
                this.pongTimer = null;
            }, SOCKET_CONFIG.HEARTBEAT_INTERVAL_MS);
        }, SOCKET_CONFIG.HEARTBEAT_INTERVAL_MS);
        /* c8 ignore stop */
    }
    clearPing() {
        if (this.pingTimer) {
            clearTimeout(this.pingTimer);
            this.pingTimer = null;
        }
        if (this.pongTimer) {
            clearTimeout(this.pongTimer);
            this.pongTimer = null;
        }
    }
    handlePong() {
        if (this.pongTimer) {
            clearTimeout(this.pongTimer);
            this.pongTimer = null;
        }
        this.ping();
    }
    // get the data which we expected from nested structure of the message
    handleMessage(evt) {
        const filteredEvent = SocketStoreBase.messageFilters.reduce((currentEvent, filter) => {
            if (!SocketStoreBase.isSocketMessageEvent(currentEvent)) {
                return currentEvent;
            }
            return filter(currentEvent);
        }, evt);
        if (!SocketStoreBase.isSocketMessageEvent(filteredEvent)) {
            psLog.warn('Failed to parse message, invalid message event received.');
            return;
        }
        const { data: rawData } = filteredEvent;
        if (typeof rawData !== 'string') {
            psLog.warn('Failed to parse message, expected string data.');
            return;
        }
        const { CONNECT, MESSAGE, ERROR, JOIN, PING, PONG, LEAVE, CLOSE, BROADCAST, } = SERVER_MESSAGE_TYPE;
        let result;
        try {
            result = JSON.parse(rawData);
        }
        catch (e) {
            psLog.warn('Failed to parse message, malformed data received.');
            return;
        }
        const { type } = result;
        switch (type) {
            case CONNECT:
                const { selfConnection, roomConnections } = result.content;
                this.socketConnection = selfConnection;
                this.debuggerConnection =
                    roomConnections.find((i) => i.userId === 'Debugger') || null;
                break;
            case JOIN:
            case LEAVE:
                const { connection } = result.content;
                if (connection.userId === 'Debugger') {
                    if (type === JOIN) {
                        this.debuggerConnection = connection;
                        // once connected, send client info
                        this.sendClientInfo();
                    }
                    else {
                        this.debuggerConnection = null;
                    }
                }
                break;
            case MESSAGE:
                const { data, from, to } = result.content;
                if (to.address === this.socketConnection?.address &&
                    SocketStoreBase.isInteractiveType(data.type)) {
                    this.dispatchEvent(data.type, {
                        source: data,
                        from,
                        to,
                    });
                }
                break;
            case CLOSE:
            case ERROR:
                this.connectOffline();
                break;
            /* c8 ignore start */
            case PONG:
            case PING:
            case BROADCAST:
            default:
                // noting
                break;
            /* c8 ignore stop */
        }
        // whatever the type is, we should handle pong
        this.handlePong();
    }
    dispatchEvent(type, data) {
        if (['public-data'].includes(type)) {
            this.events['public-data'].forEach((fn) => {
                fn(data);
            });
            return;
        }
        this.events[type]?.forEach((fn) => {
            fn.call(this, data, (d) => {
                this.unicastMessage(d, data.from);
            });
        });
    }
    unicastMessage(msg, to) {
        const message = makeUnicastMessage(msg, this.socketConnection, to);
        this.send(message);
    }
    handleFlushBuffer(message) {
        const { latestId } = message.source.data;
        const msgIndex = this.messages.findIndex((i, idx) => idx >= this.messageHead && i.content.data.data.id === latestId);
        /* c8 ignore start */
        this.messages.slice(msgIndex + 1).forEach((msg) => {
            const data = {
                type: SERVER_MESSAGE_TYPE.MESSAGE,
                content: {
                    data: msg.content.data,
                    from: this.socketConnection,
                    to: message.from,
                },
            };
            this.send(data, true);
        });
        /* c8 ignore stop */
    }
    static handleResolveAtom({ source }, reply) {
        const { type, data } = source;
        if (type === 'atom-detail') {
            const atomData = atom.get(data) || {};
            const msg = makeMessage(`atom-detail-${data}`, atomData, false);
            reply(msg);
        }
    }
    static handleAtomPropertyGetter({ source }, reply) {
        const { type, data } = source;
        if (type === 'atom-getter') {
            const { id, parentId, key, instanceId } = data;
            const instance = atom.getOrigin(instanceId);
            const current = atom.getOrigin(parentId);
            let value = {};
            /* c8 ignore start */
            if (instance && current) {
                value = Object.getOwnPropertyDescriptor(current, key)?.get?.call(instance);
            }
            else {
                value = new Error('Getter computed failed');
            }
            /* c8 ignore stop */
            const msg = makeMessage(`atom-getter-${id}`, atom.transformToAtom(value));
            reply(msg);
        }
    }
    send(msg, noCache = false) {
        const sendable = this.checkIfSend(msg);
        if (sendable) {
            /* c8 ignore start */
            try {
                const pkMsg = msg;
                pkMsg.createdAt = Date.now();
                pkMsg.requestId = getRandomId();
                const dataString = stringifyData(pkMsg);
                this.socketWrapper?.send(dataString);
            }
            catch (e) {
                psLog.error(`Incompatible: ${e instanceof Error ? e.message : String(e)}`);
                this.connectOffline();
            }
            /* c8 ignore stop */
        }
        const cacheable = this.checkIfCache(msg, noCache);
        if (cacheable) {
            // FIFO eviction: when buffer is full, advance the head pointer
            if (this.messageCapacity !== 0 &&
                this.messages.length - this.messageHead >= this.messageCapacity) {
                this.messageHead += 1;
                // Compact the array periodically to prevent unbounded growth
                // Once half the array is unused, slice it off
                if (this.messageHead > this.messageCapacity) {
                    this.messages = this.messages.slice(this.messageHead);
                    this.messageHead = 0;
                }
            }
            this.messages.push(msg);
        }
    }
    checkIfSend(msg) {
        if (this.socketWrapper.getState() !== SocketState.OPEN)
            return false;
        if ([SERVER_MESSAGE_TYPE.UPDATE_ROOM_INFO, SERVER_MESSAGE_TYPE.PING].includes(msg.type)) {
            return true;
        }
        if (!this.debuggerConnection)
            return false;
        return true;
    }
    checkIfCache(msg, noCache = false) {
        if (this.isOffline || noCache)
            return false;
        if ([SERVER_MESSAGE_TYPE.MESSAGE, SERVER_MESSAGE_TYPE.PING].includes(msg.type)) {
            return false;
        }
        return true;
    }
    sendClientInfo() {
        const clientInfo = this.getClient?.().makeClientInfoMsg();
        this.broadcastMessage({
            role: 'client',
            type: 'client-info',
            data: clientInfo,
        }, true);
    }
    static isSocketMessageEvent(value) {
        return typeof value === 'object' && value !== null && 'data' in value;
    }
    static isInteractiveType(type) {
        return (type === 'debug' ||
            type === 'refresh' ||
            type === 'atom-detail' ||
            type.startsWith('atom-detail-') ||
            type === 'atom-getter' ||
            type.startsWith('atom-getter-') ||
            type === 'debugger-online' ||
            type === 'database-pagination');
    }
}
